import React, { useContext, useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import MercadoPagoService from '../../service/mercado-pago.service';

const MP_LOGO = 'https://http2.mlstatic.com/frontend-assets/ui-navigation/5.19.1/mercadolibre/logo__small@2x.png';

function IntegrationsPage() {
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  const accountId = account?.id;
  const onlinePaymentAllowed = account?.features?.onlinePayment !== false;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    publicKey: '',
    accessToken: '',
    webhookSecret: '',
  });

  const load = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await MercadoPagoService.getIntegration(accountId);
      setIntegration(data);
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar integrações', 'icon-alert-circle-exc');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [accountId]);

  const openModal = () => {
    setForm({ publicKey: '', accessToken: '', webhookSecret: '' });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.publicKey.trim() || !form.accessToken.trim() || !form.webhookSecret.trim()) {
      notify('tr', 'warning', 'Preencha todas as credenciais', 'icon-alert-circle-exc');
      return;
    }
    setSaving(true);
    try {
      const data = await MercadoPagoService.saveIntegration(accountId, form);
      setIntegration(data);
      setModalOpen(false);
      notify('tr', 'success', 'Mercado Pago conectado com sucesso', 'icon-check-2');
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao salvar integração', 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar o Mercado Pago desta conta?')) return;
    setSaving(true);
    try {
      await MercadoPagoService.removeIntegration(accountId);
      await load();
      notify('tr', 'success', 'Integração removida', 'icon-check-2');
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao desconectar', 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = async () => {
    if (!integration?.webhookUrl) return;
    try {
      await navigator.clipboard.writeText(integration.webhookUrl);
      notify('tr', 'success', 'URL copiada', 'icon-check-2');
    } catch {
      notify('tr', 'warning', 'Não foi possível copiar', 'icon-alert-circle-exc');
    }
  };

  const connected = integration?.connected;

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Integrações</CardTitle>
              <p className="card-category mb-0">
                Conecte serviços de pagamento e automação ao seu estabelecimento.
              </p>
            </CardHeader>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="6" xl="4">
          <Card className="card-stats" style={{ minHeight: 280 }}>
            <CardBody>
              <div className="d-flex align-items-start justify-content-between mb-3">
                <img src={MP_LOGO} alt="Mercado Pago" style={{ height: 32 }} />
                <Badge color={connected ? 'success' : 'secondary'} pill>
                  {connected ? 'Conectado' : 'Desconectado'}
                </Badge>
              </div>
              <h5 className="card-title mb-2">Mercado Pago</h5>
              <p className="text-muted mb-4" style={{ fontSize: 14, minHeight: 60 }}>
                Receba pagamentos online via Checkout Pro (PIX, cartão e mais) diretamente no cardápio digital.
              </p>

              {!onlinePaymentAllowed ? (
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  Pagamento online não está disponível no seu plano atual.
                </p>
              ) : loading ? (
                <p className="text-muted mb-0">Carregando...</p>
              ) : (
                <div className="d-flex gap-2 flex-wrap">
                  <Button color="primary" size="sm" onClick={openModal} disabled={saving}>
                    {connected ? 'Atualizar credenciais' : 'Conectar'}
                  </Button>
                  {connected && (
                    <Button color="danger" outline size="sm" onClick={handleDisconnect} disabled={saving}>
                      Desconectar
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {connected && integration?.webhookUrl && (
        <Row>
          <Col lg="8">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Configuração do Webhook</CardTitle>
                <p className="card-category mb-0">
                  No painel do Mercado Pago: Suas integrações → Webhooks
                </p>
              </CardHeader>
              <CardBody>
                <FormGroup className="mb-3">
                  <Label className="mb-2">URL de notificação</Label>
                  <InputGroup>
                    <Input
                      readOnly
                      value={integration.webhookUrl}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        color: '#e3e3e3',
                        borderRight: 0,
                      }}
                    />
                    <Button color="primary" onClick={copyWebhook} style={{ minWidth: 100 }}>
                      <i className="tim-icons icon-single-copy-04 mr-1" />
                      Copiar
                    </Button>
                  </InputGroup>
                </FormGroup>

                <div
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 8,
                    padding: '14px 18px',
                    fontSize: 13,
                  }}
                >
                  <p className="mb-2" style={{ fontWeight: 600, color: '#ccc' }}>
                    Passos no Mercado Pago
                  </p>
                  <ol className="mb-3 pl-3" style={{ color: '#999', lineHeight: 1.8 }}>
                    <li>Cole a URL acima no campo de webhook (modo teste ou produção)</li>
                    <li>Ative o evento <strong style={{ color: '#e3e3e3' }}>Pagamentos</strong> (ou Pagamentos legacy)</li>
                    <li>PIX pode demorar alguns segundos — o pedido confirma via webhook automaticamente</li>
                    <li>Copie a assinatura secreta gerada e salve no formulário de conexão</li>
                  </ol>
                  <p className="mb-0" style={{ color: '#999', fontSize: 12, lineHeight: 1.6 }}>
                    Se no checkout do MP aparecer &quot;Voltar para uber&quot; (ou outro nome estranho), isso vem da{' '}
                    <strong style={{ color: '#ccc' }}>aplicação no painel do Mercado Pago</strong>, não do nosso sistema.
                    Altere em{' '}
                    <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noopener noreferrer">
                      Suas integrações → nome da aplicação
                    </a>{' '}
                    ou em Mercado Pago → Seu negócio → Dados do negócio.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          Conectar Mercado Pago
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            Informe as credenciais de teste ou produção do painel de desenvolvedores do Mercado Pago.
          </p>
          <Form>
            <FormGroup>
              <Label>Public Key</Label>
              <Input
                value={form.publicKey}
                onChange={(e) => setForm({ ...form, publicKey: e.target.value })}
                placeholder="APP_USR-..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Access Token</Label>
              <Input
                type="password"
                value={form.accessToken}
                onChange={(e) => setForm({ ...form, accessToken: e.target.value })}
                placeholder="APP_USR-..."
              />
            </FormGroup>
            <FormGroup>
              <Label>Assinatura secreta (Webhook)</Label>
              <Input
                type="password"
                value={form.webhookSecret}
                onChange={(e) => setForm({ ...form, webhookSecret: e.target.value })}
                placeholder="Chave do painel de webhooks"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModalOpen(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default IntegrationsPage;
